<template>
  <div v-if="canShowOrderSummary" class="order-totals-container">
    <div class="order-totals-header">
      <div class="order-totals-header__title" @click="toggleSummary()">
        <span v-if="isExpanded">Hide</span><span v-else>Show</span> Billing Summary
        <svg :class="{'rotated': !isExpanded}" xmlns="http://www.w3.org/2000/svg" width="36" height="28" viewBox="0 0 36 28" fill="none">
          <path d="M32 0H4C1.79086 0 0 1.79086 0 4V24C0 26.2091 1.79086 28 4 28H32C34.2091 28 36 26.2091 36 24V4C36 1.79086 34.2091 0 32 0Z" fill="white"/>
          <path d="M11 17.3266L12.6734 19L18.12 13.5653L23.5666 19L25.24 17.3266L18.12 10.2066L11 17.3266Z" fill="#163549"/>
        </svg>
      </div>
      <div class="order-totals-header__total" v-if="!isExpanded">
        {{order.total_discounted_price | currency(order.currency)}}
      </div>
    </div>
    <hr :class="{'mb-0': !isExpanded}" />
    <div class="order-totals-info" v-if="isExpanded">
      <div class="order-totals-info__container">
        <div class="order-totals-info__item">
          <span class="total-heading">Subtotal:</span>
          <span class="notranslate total-text">{{order.subtotal | currency(order.currency)}}</span>
        </div>
        <div class="order-totals-info__item" v-if="showShipping(order)">
          <span class="total-heading">Shipping:</span>
          <span class="notranslate total-text">{{order.shipping_price | currency(order.currency)}}</span>
        </div>
        <div class="order-totals-info__item" v-if="showHiddenFees(order)">
          <span class="total-heading">{{session.hidden_fees_label}}:</span>
          <span class="notranslate total-text">{{order.hidden_fees | currency(order.currency)}}</span>
        </div>
        <div class="order-totals-info__item" v-if="showDiscount(order)">
          <span class="total-heading">Discount:</span>
          <span class="notranslate total-text cancelled">-{{order.discount | currency(order.currency)}}</span>
        </div>
        <div class="order-totals-info__item" v-if="Number(order.tax) > 0">
          <span class="total-heading">Tax:</span>
          <span class="notranslate total-text">{{order.tax | currency(order.currency)}}</span>
        </div>
        <div class="order-totals-info__item" v-if="Number(order.tip) > 0">
          <span class="total-heading">Tip:</span>
          <span class="notranslate total-text">{{order.tip | currency(order.currency)}}</span>
        </div>
        <hr class="mt-0 order-totals-info__item w-100" />
        <div class="order-totals-info__item" >
          <div class="label total-heading fw-500">Total:</div>
          <div class="notranslate total-text">{{order.total_discounted_price | currency(order.currency)}}</div>
        </div>
      </div>
      <hr />
      <div class="order-totals-info__container">
        <div class="order-totals-info__item" v-if="showPastBalance(order)">
          <div class="total-heading">Past Payments:</div>
          <div class="notranslate total-text">{{order.past_balance | currency(order.currency)}}</div>
        </div>
        <div class="order-totals-info__item" v-if="showForwardBalance(order)">
          <div class="total-heading">Carry Forward:</div>
          <div class="notranslate total-text cancelled">-{{order.forward_balance | currency(order.currency)}}</div>
        </div>
        <div class="order-totals-info__item">
          <div class="total-heading">Amount Paid:</div>
          <div class="notranslate total-text">{{order.amount_paid | currency(order.currency)}}</div>
        </div>
        <div class="order-totals-info__item" v-if="showStoreDebit(order)">
          <div class="total-heading">Credited:</div>
          <div class="notranslate total-text">{{order.store_debit | currency(order.currency)}}</div>
        </div>
        <div class="order-totals-info__item" v-if="showVoid(order)">
          <div class="total-heading">Voided:</div>
          <div class="notranslate total-text cancelled">-{{order.amount_voided | currency(order.currency)}}</div>
        </div>
        <div class="order-totals-info__item" v-if="showRefund(order)">
          <div class="total-heading">Refunded:</div>
          <div class="notranslate total-text cancelled">-{{order.settled_refund_amount | currency(order.currency)}}</div>
        </div>
        <div class="order-totals-info__item" v-if="showPendingRefund(order)">
          <div class="total-heading">
            <b-tooltip target="help" triggers="hover" placement="top">
              The refund is pending because the payment has not been captured yet.<br/>
              We will be able to process the refund once the charge is captured.<br/>
              This may take a few business days to appear.<br/>
            </b-tooltip>
            Pending Refund: <img id="help" :src="require('@/assets/images/help.svg')"/>
          </div>
          <div class="notranslate total-text pending">-{{order.pending_refund_amount | currency(order.currency)}}</div>
        </div>
        <div class="order-totals-info__item" v-if="showStoreCredit(order)">
          <div class="total-heading">Store Credit:</div>
          <div class="notranslate total-text cancelled">-{{order.store_credit | currency(order.currency)}}</div>
        </div>
        <div class="order-totals-info__item" v-if="showChargeback(order)">
          <div class="total-heading">Chargeback:</div>
          <div class="notranslate total-text cancelled">-{{order.chargeback_amount | currency(order.currency)}}</div>
        </div>
        <hr class="mt-0 order-totals-info__item w-100" />
        <div class="order-totals-info__item" >
          <div class="label total-heading fw-500">Total Paid:</div>
          <div class="notranslate total-text">{{order.total_remaining_balance | currency(order.currency)}}</div>
        </div>
        <div class="order-totals-info__item" v-if="showAmountDue(order)">
          <div class="label total-heading fw-500">Amount Due:</div>
          <div class="notranslate total-text">{{order.amount_due | currency(order.currency)}}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState} from 'vuex'

export default {
  name: 'OrderTotalsInfo',
  props: {
    order: Object
  },
  data: function() {
    return {
      isExpanded: false
    }
  },
  computed: {
    ...mapState(['session']),
    canShowOrderSummary: function() {
      return this.session && this.session.show_order_summary
    }
  },
  methods: {
    toggleSummary: function() {
      this.isExpanded = !this.isExpanded
    },
    showRefund: function(order) {
      return parseFloat(order.settled_refund_amount) > Number.EPSILON
    },
    showVoid: function(order) {
      return parseFloat(order.amount_voided) > Number.EPSILON
    },
    showPendingRefund: function(order) {
      return parseFloat(order.pending_refund_amount) > Number.EPSILON
    },
    showChargeback: function(order) {
      return parseFloat(order.chargeback_amount) > Number.EPSILON
    },
    showAmountDue: function(order) {
      return parseFloat(order.amount_due) > Number.EPSILON
    },
    showDiscount: function(order) {
      return parseFloat(order.discount) > Number.EPSILON
    },
    showHiddenFees: function(order) {
      return parseFloat(order.hidden_fees) > Number.EPSILON
    },
    showShipping: function(order) {
      return (parseFloat(order.shipping_price) > Number.EPSILON) || !this.showHiddenFees(order)
    },
    showForwardBalance: function(order) {
      return parseFloat(order.forward_balance) > Number.EPSILON
    },
    showPastBalance: function(order) {
      return parseFloat(order.past_balance) > Number.EPSILON
    },
    showStoreCredit: function(order) {
      return parseFloat(order.store_credit) > Number.EPSILON
    },
    showStoreDebit: function(order) {
      return parseFloat(order.store_debit) > Number.EPSILON
    }
  }
}
</script>

<style lang="less">
  .order-totals-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 260px;
    margin-bottom: 30px;

    hr {
      width: 100%;
      margin: 15px 0;
    }

    @media (min-width: 768px) {
      width: 40%;
      min-width: 300px;
      float: right;
    }

    @media (min-width: 992px) {
      width: 40%;
      float: right;
    }

    @media (min-width: 1200px) {
      width: 35%;
      float: right;
    }

    .order-totals-header {
      display: flex;
      font-size: 16px;
      font-weight: 500;
      color: #163549;
      align-items: center;
      justify-content: space-between;

      &__title {
        color: #333;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        cursor: pointer;

        svg {
          transition: transform 0.2s;

          &.rotated {
            transform: rotate(180deg);
          }
        }

        @media (min-width: 414px) {
          font-size: 16px;
        }
      }

      &__total {
        color: #333;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
      }
    }

    .order-totals-info {
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: 15px;

      &__container {
        display: flex;
        flex-direction: column;
        gap: 15px;
        flex: 1;

        &:last-child {
          margin-bottom: 15px;

          @media (min-width: 768px) {
            margin-bottom: 0;
          }
        }
      }

      hr {
        width: 100%;
        margin: 0;
      }

      &__item {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        color: #333;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;

        .total-text {
          font-size: 16px;

          &.active {
            color: #10B981;
          }

          &.cancelled {
            color: #EF4444;
          }

          &.pending {
            color: #F2BF35;
          }
        }

        &:last-child {
          .total-text {
            font-weight: 700;
            font-size: 18px;
          }
        }

        @media (min-width: 768px) {
          font-size: 16px;
        }
      }
    }
  }
</style>
